<template>
  <section class="share-overlay container">
    <div class="share-overlay__inner">
      <h2 class="header">{{ helperStore.i18n('share_options_heading') }}</h2>

      <el-row class="option-row" type="flex" align="middle" @click="copyToClipboard">
        <i class="icon-Copy"></i>
        <span>{{ helperStore.i18n('share_options_copy_link') }}</span>
      </el-row>

      <el-row class="option-row" type="flex" align="middle" @click="shareViaEmail">
        <i class="icon-Mail"></i>
        <span>{{ helperStore.i18n('share_options_email') }}</span>
      </el-row>

      <el-row class="option-row" type="flex" align="middle" @click="shareViaSms">
        <i class="icon-Message"></i>
        <span>{{ helperStore.i18n('share_options_message') }}</span>
      </el-row>

      <el-row class="option-row" type="flex" align="middle" @click="shareViaWhatsApp">
        <i class="icon-WhatsApp"></i>
        <span>WhatsApp</span>
      </el-row>

      <el-row
        v-if="isShareableViaDevice"
        @click="deviceShare"
        class="option-row"
        type="flex"
        align="middle"
      >
        <i class="icon-Add"></i>
        <span>{{ helperStore.i18n('share_options_more_options') }}</span>
      </el-row>

      <i class="icon-Cross" @click="emit('close')"></i>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { ElNotification } from 'element-plus'
import { useHelperStore } from '@/pinia/helper.store'
import { useMainStore } from '@/pinia/main.store'

const helperStore = useHelperStore()
const mainStore = useMainStore()
const props = defineProps<{ shareLink: string }>()
const guideName = computed(() => encodeURIComponent(mainStore.propertyInfo.name))
const defaultGuideMessage = computed(
  () => `Check out this guide to ${guideName.value}: ${props.shareLink}`
)
const emit = defineEmits(['close'])

const isShareableViaDevice = !!navigator.share

function copyToClipboard() {
  const el = document.createElement('textarea')

  el.value = props.shareLink
  el.style.top = '0'
  el.style.left = '0'
  el.style.position = 'fixed'

  document.body.appendChild(el)

  el.focus()
  el.select()
  el.setSelectionRange(0, 99999) /* For mobile devices */

  document.execCommand('copy')
  document.body.removeChild(el)

  ElNotification.success('Copied to clipboard!')
  emit('close')
}

function shareViaEmail() {
  const link = new URL(`mailto:?subject=${guideName.value}&body=${defaultGuideMessage.value}`)

  const el = document.createElement('a')
  el.href = link.toString()
  el.dispatchEvent(new MouseEvent('click'))
  emit('close')
}

function shareViaSms() {
  const link = new URL(`sms:?&body=${defaultGuideMessage.value}`)
  const el = document.createElement('a')
  el.href = link.toString()
  el.dispatchEvent(new MouseEvent('click'))
  emit('close')
}

function shareViaWhatsApp() {
  const link = new URL(`https://wa.me/?text=${defaultGuideMessage.value}`)
  const el = document.createElement('a')
  el.href = link.toString()
  el.dispatchEvent(new MouseEvent('click'))
  emit('close')
}

function deviceShare() {
  try {
    navigator.share({
      title: `Check out this guide to ${guideName.value}`,
      url: props.shareLink
    })
  } catch (e) {
    console.warn('share cancelled')
  }

  emit('close')
}
</script>

<style scoped lang="scss">
.share-overlay {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  z-index: 999;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  opacity: 1;

  @include mediaMobile {
    width: 100%;
    top: 0;
    height: calc(100% - #{$header-height});
  }

  &__inner {
    position: relative;
    padding: 30px;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;

    .header {
      text-align: center;
      font-size: 1.25rem;
      margin: 0;
    }

    .option-row {
      width: 100%;
      padding: 15px;
      border: 1px solid $border-color;
      margin-top: 20px;
      border-radius: $border-radius;
      box-shadow: 0 0 5px 1px rgb(0 0 0 / 10%);

      span {
        font-weight: bold;
      }

      i {
        font-size: 1.375rem;
        font-weight: bold;
        color: v-bind('helperStore.styleData.accentColor');
        padding-right: 15px;
      }
    }

    .icon-Cross {
      position: absolute;
      right: 10px;
      top: 10px;
      font-size: 1.5rem;
    }
  }
}
</style>
